import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BudgetOverviewCard from './components/BudgetOverviewCard';
import DepartmentBudgetTable from './components/DepartmentBudgetTable';
import PayrollCostAnalysis from './components/PayrollCostAnalysis';
import ForecastingPanel from './components/ForecastingPanel';
import ComplianceImpactCalculator from './components/ComplianceImpactCalculator';
import SpendingTrendChart from './components/SpendingTrendChart';
import departmentalBudgetService from '../../services/departmentalBudgetService';
import EmptyStatePrompt from '../../components/EmptyStatePrompt';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';

const DepartmentalBudgetManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [budgets, setBudgets] = useState([]);
  const [overview, setOverview] = useState(null);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [payrollBreakdown, setPayrollBreakdown] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [complianceImpacts, setComplianceImpacts] = useState([]);
  const [spendingTrends, setSpendingTrends] = useState([]);
  const [filters, setFilters] = useState({
    department: '',
    dateRange: { start: '', end: '' },
    employmentType: ''
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    loadData();
    checkSubscription();
  }, [filters?.department]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [budgetsData, overviewData] = await Promise.all([
        departmentalBudgetService?.getAllBudgets({ department: filters?.department }),
        departmentalBudgetService?.getBudgetOverview()
      ]);

      setBudgets(budgetsData || []);
      setOverview(overviewData);

      if (budgetsData?.length > 0 && !selectedBudget) {
        setSelectedBudget(budgetsData?.[0]);
        await loadBudgetDetails(budgetsData?.[0]?.id);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load budget data');
    } finally {
      setLoading(false);
    }
  };

  const loadBudgetDetails = async (budgetId) => {
    try {
      const [payroll, forecastData, compliance, trends] = await Promise.all([
        departmentalBudgetService?.getPayrollBreakdown(budgetId),
        departmentalBudgetService?.getForecasts(budgetId),
        departmentalBudgetService?.getComplianceCostImpact(budgetId),
        departmentalBudgetService?.getSpendingTrends(budgetId)
      ]);

      setPayrollBreakdown(payroll || []);
      setForecasts(forecastData || []);
      setComplianceImpacts(compliance || []);
      setSpendingTrends(trends || []);
    } catch (err) {
      console.error('Failed to load budget details:', err);
    }
  };

  const handleSelectBudget = async (budget) => {
    setSelectedBudget(budget);
    await loadBudgetDetails(budget?.id);
    setActiveTab('payroll');
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleRefresh = () => {
    loadData();
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading budget data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!budgets || budgets?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
              Departmental Budget Management
            </h1>
            <p className="caption text-sm md:text-base text-muted-foreground">
              Track departmental budgets, payroll costs in PNG Kina, and financial forecasting with compliance impact analysis
            </p>
          </div>

          <div className="bg-card rounded-lg border border-border p-8">
            <EmptyStatePrompt
              icon="DollarSign"
              title="No Budget Data Available"
              description="Create departmental budgets to track spending, payroll costs, and forecast financial requirements with compliance impact analysis."
              actionLabel="Create Budget"
              onAction={() => alert('Budget creation feature coming soon')}
            />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-6 md:mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                Departmental Budget Management
              </h1>
              <p className="caption text-sm md:text-base text-muted-foreground">
                Track departmental budgets, payroll costs in PNG Kina, and financial forecasting with compliance impact analysis
              </p>
            </div>
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Icon name="RefreshCw" size={18} color="currentColor" />
              Refresh
            </Button>
          </div>

          <GlobalFilterBar onFilterChange={handleFilterChange} />
          <ComplianceAlertBanner />
          <DataRefreshIndicator />
        </div>

        {error && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} color="var(--color-primary)" />
              <p className="text-blue-800 font-medium">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <BudgetOverviewCard
            title="Total Allocated Budget"
            value={departmentalBudgetService?.formatKina(overview?.totalAllocated)}
            subtitle={`Across ${overview?.totalDepartments} departments`}
            icon="DollarSign"
            iconColor="var(--color-primary)"
            trend={null}
            trendValue={null}
          />
          <BudgetOverviewCard
            title="Total Spent"
            value={departmentalBudgetService?.formatKina(overview?.totalSpent)}
            subtitle={`${overview?.utilizationPercentage?.toFixed(1)}% utilization`}
            icon="TrendingUp"
            iconColor="var(--color-success)"
            trend={overview?.utilizationPercentage > 75 ? 'negative' : 'positive'}
            trendValue={`${overview?.utilizationPercentage?.toFixed(1)}%`}
          />
          <BudgetOverviewCard
            title="Remaining Balance"
            value={departmentalBudgetService?.formatKina(overview?.totalRemaining)}
            subtitle="Available for allocation"
            icon="Wallet"
            iconColor="var(--color-primary)"
            trend={null}
            trendValue={null}
          />
          <BudgetOverviewCard
            title="Total Payroll Costs"
            value={departmentalBudgetService?.formatKina(overview?.totalPayroll)}
            subtitle="Employee compensation"
            icon="Users"
            iconColor="var(--color-primary)"
            trend={null}
            trendValue={null}
          />
        </div>

        <div className="bg-card border border-border rounded-lg mb-6">
          <div className="border-b border-border">
            <div className="flex items-center gap-2 p-2 overflow-x-auto">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-2 rounded-md transition-smooth whitespace-nowrap ${
                  activeTab === 'overview' ?'bg-primary text-primary-foreground font-medium' :'text-muted-foreground hover:bg-muted'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon name="LayoutDashboard" size={18} color="currentColor" />
                  Budget Overview
                </div>
              </button>
              <button
                onClick={() => setActiveTab('payroll')}
                className={`px-4 py-2 rounded-md transition-smooth whitespace-nowrap ${
                  activeTab === 'payroll' ?'bg-primary text-primary-foreground font-medium' :'text-muted-foreground hover:bg-muted'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon name="Users" size={18} color="currentColor" />
                  Payroll Analysis
                </div>
              </button>
              <button
                onClick={() => setActiveTab('forecasting')}
                className={`px-4 py-2 rounded-md transition-smooth whitespace-nowrap ${
                  activeTab === 'forecasting'
                    ? 'bg-primary text-primary-foreground font-medium' :'text-muted-foreground hover:bg-muted'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon name="TrendingUp" size={18} color="currentColor" />
                  Forecasting
                </div>
              </button>
              <button
                onClick={() => setActiveTab('compliance')}
                className={`px-4 py-2 rounded-md transition-smooth whitespace-nowrap ${
                  activeTab === 'compliance' ?'bg-primary text-primary-foreground font-medium' :'text-muted-foreground hover:bg-muted'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon name="Shield" size={18} color="currentColor" />
                  Compliance Impact
                </div>
              </button>
              <button
                onClick={() => setActiveTab('trends')}
                className={`px-4 py-2 rounded-md transition-smooth whitespace-nowrap ${
                  activeTab === 'trends' ?'bg-primary text-primary-foreground font-medium' :'text-muted-foreground hover:bg-muted'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon name="LineChart" size={18} color="currentColor" />
                  Spending Trends
                </div>
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <DepartmentBudgetTable budgets={budgets} onSelectBudget={handleSelectBudget} />
            )}

            {activeTab === 'payroll' && selectedBudget && (
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-heading font-semibold text-foreground">
                    {selectedBudget?.department} - Payroll Cost Analysis
                  </h2>
                  <p className="text-sm text-muted-foreground">{selectedBudget?.fiscalPeriod}</p>
                </div>
                <PayrollCostAnalysis payrollBreakdown={payrollBreakdown} />
              </div>
            )}

            {activeTab === 'forecasting' && selectedBudget && (
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-heading font-semibold text-foreground">
                    {selectedBudget?.department} - Financial Forecasting
                  </h2>
                  <p className="text-sm text-muted-foreground">Projected budget requirements based on historical data and compliance impacts</p>
                </div>
                <ForecastingPanel forecasts={forecasts} />
              </div>
            )}

            {activeTab === 'compliance' && selectedBudget && (
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-heading font-semibold text-foreground">
                    {selectedBudget?.department} - Compliance Cost Impact Analysis
                  </h2>
                  <p className="text-sm text-muted-foreground">Financial implications of PNG Employment Act, NASFUND, and SWT compliance</p>
                </div>
                <ComplianceImpactCalculator complianceImpacts={complianceImpacts} />
              </div>
            )}

            {activeTab === 'trends' && selectedBudget && (
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-heading font-semibold text-foreground">
                    {selectedBudget?.department} - Spending Trends
                  </h2>
                  <p className="text-sm text-muted-foreground">Monthly spending patterns and budget utilization analysis</p>
                </div>
                <SpendingTrendChart trends={spendingTrends} />
              </div>
            )}
          </div>
        </div>
      </main>
      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </div>
  );
};

export default DepartmentalBudgetManagement;